import { defineStore } from "pinia";
import { ref, watch, computed, shallowRef } from "vue";
import { currencySymbol } from "@/constants/country_currency.js";
import { currencyFormat } from "@/pages/LoyaltyProgram/RedeemingPoints/Reusables/functions";
import { useStampCardStore } from "./stampCard";
import { PointBracket } from "@/utilities/PointBracket.js";

import BrandSetup from "@/pages/AccountManagement/UserOnboarding/BrandSetup.vue";
import CustomerSpend from "@/pages/AccountManagement/UserOnboarding/CustomerSpend.vue";
import StampSetup from "@/pages/AccountManagement/UserOnboarding/StampSetup.vue";
import PointSetup from "@/pages/AccountManagement/UserOnboarding/EarnPoints.vue";
import RewardSetup from "@/pages/AccountManagement/UserOnboarding/RedeemPoints2.vue";
import AccountCreds from "@/pages/AccountManagement/UserOnboarding/AccountCreds.vue";
import cookie from "@/utilities/cookie";

export const useBrandStore = defineStore("brand", () => {
  const brand = ref(null);
  const extraQuery = ref(null);
  const query = ref(null);
  const totalUnsentMessages = ref(0);
  const messageInQueue = ref(0);
  const websiteToken = ref(null);
  const userBrandData = ref([]);
  const invitationTrigger = ref(null);
  const minimumSingleRecharge = ref(0);
  const minimumAutoRecharge = ref(0);
  const threshold = ref(0);
  const collectionType = ref("");
  const hasAutoOpenedOmnibox = ref(false);
  const companies = ref([]);
  const slug = computed(() => brand.value?.slug || "");

  watch(brand, (newBrand) => {
    if (newBrand) {
      totalUnsentMessages.value = newBrand.totalUnsentMessages || 0;
      messageInQueue.value = newBrand.messageInQueue || 0;
      websiteToken.value = newBrand.websiteToken || "";
      minimumSingleRecharge.value =
        newBrand.minimumRechargeValue.defaultSingleCredit || 0;
      minimumAutoRecharge.value =
        newBrand.minimumRechargeValue.defaultAutoCredit || 0;
      threshold.value =
        newBrand.minimumRechargeValue.defaultRechargeThreshold || 0;
      collectionType.value = newBrand.collectionType || "";
    }
  });

  const outlets = ref([]);
  const getStores = computed(() => outlets.value);

  const currSym = computed(() => {
    return brand.value?.country?.spendingCurrency?.symbol || "$";
  });
  // use currency code as billing symbol
  const billingCurrSym = computed(() => {
    return brand.value?.country?.currency?.code || "USD";
  });
  const currCode = computed(() => {
    return brand.value?.country?.spendingCurrency?.code || "USD";
  });
  const billingCurrCode = computed(() => {
    return brand.value?.country?.currency?.code || "USD";
  });

  const totalMessagesOnHold = computed(() => {
    return totalUnsentMessages.value + messageInQueue.value;
  });

  const unmatchedRewardsCount = computed(() => {
    return brand.value?.unmatchedRewards;
  });

  const isBetaTester = computed(() => {
    return (
      brand.value?.role === "tester" ||
      brand.value?.role === "beta_tester" ||
      brand.value?.role === "automated_tester"
    );
  });

  const isQaTester = computed(() => {
    return (
      brand.value?.role === "tester" || brand.value?.role === "automated_tester"
    );
  });

  const isPointsCollection = computed(() => {
    return brand.value?.collectionType === "points";
  });

  const isPartOfGroup = computed(() => {
    return !!brand.value?.brandGroup;
  });

  const pointsName = computed(() => {
    return brand.value?.brandConfiguration?.pointsName || "Points";
  });

  const pointBracket = computed(() => new PointBracket(pointsName.value));

  const isRestricted = computed(() => {
    return brand.value?.country?.smsFeatureBlocked || false;
  });

  const isOtpRestricted = computed(() => {
    return brand.value?.country?.smsOtpBlocked || false;
  });

  const activeBrandLogoUrl = computed(() => {
    const activeBrandId = cookie.getSecureCookie("mulah_br_id");
    const activeBrand = userBrandData.value.find(
      (brand) => brand.id === activeBrandId
    );
    return activeBrand ? activeBrand.logoUrl : "";
  });

  const isEmailBroadcastDisabled = computed(() => {
    return brand.value?.brandConfiguration?.needEmailVerification === false;
  });

  async function getBrand() {
    if (brand.value == null) {
      const res = await query.value.executeQuery();
      if (res.data && res.data.value.slBrand) {
        brand.value = res.data.value.slBrand;
      }
    }
  }

  function setUrgentConversationCount(count) {
    if (!brand.value) return;
    brand.value.urgentConversationCount = count;
  }

  function $reset() {
    brand.value = null;
    totalUnsentMessages.value = 0;
    messageInQueue.value = 0;
    websiteToken.value = null;
    userBrandData.value = [];
    minimumSingleRecharge.value = 0;
    minimumAutoRecharge.value = 0;
    threshold.value = 0;
    collectionType.value = "";
    hasAutoOpenedOmnibox.value = false;
    companies.value = [];
  }

  return {
    brand,
    slug,
    currSym,
    billingCurrSym,
    query,
    extraQuery,
    outlets,
    getStores,
    getBrand,
    setUrgentConversationCount,
    hasAutoOpenedOmnibox,
    totalUnsentMessages,
    messageInQueue,
    totalMessagesOnHold,
    collectionType,
    userBrandData,
    invitationTrigger,
    websiteToken,
    unmatchedRewardsCount,
    minimumSingleRecharge,
    minimumAutoRecharge,
    threshold,
    isBetaTester,
    isQaTester,
    isPointsCollection,
    isPartOfGroup,
    currCode,
    billingCurrCode,
    $reset,
    pointsName,
    pointBracket,
    isRestricted,
    activeBrandLogoUrl,
    isOtpRestricted,
    companies,
    isEmailBroadcastDisabled,
  };
});

export const useBrandSetupStore = defineStore("brand-setup", () => {
  const stampStore = useStampCardStore();

  const brandName = ref("");
  const brandIndustry = ref({});
  const brandTimeZone = ref({});
  const brandCountry = ref({});
  const customerSpend = ref(0);
  const brandCurrency = computed(() => {
    return brandCountry.value?.currency || "";
  });
  const loyaltyProgramType = ref("points");
  const customerSpendMode = ref("per transaction");
  const valueOfRedemption = ref([]);
  const noOfVisits = ref([]);

  const activePage = shallowRef(onboardingPages[0]);

  const createdBrand = ref({});

  const isPageValid = (page) => {
    switch (page) {
      case "BrandSetup":
        return (
          brandName.value.trim() !== "" &&
          !isObjectEmpty(brandIndustry.value) &&
          !isObjectEmpty(brandCountry.value) &&
          !isObjectEmpty(brandTimeZone.value) &&
          outlets.value.length > 0
        );
      case "LoyaltySetup":
        return (
          customerSpend.value > 0 &&
          customerSpendMode.value.trim() !== "" &&
          loyaltyProgramType.value.trim() !== ""
        );
      case "RewardSetup":
        return (
          rewards.value.length > 0 &&
          isRewardValid.value &&
          brandPoints.value > 0 &&
          brandSpending.value > 0 &&
          brandEarningSelection.value.trim() !== ""
        );
      case "StampSetup": {
        const requiredFields = [stampStore.description, stampStore.totalStamps];

        const isValidTotalStamps =
          stampStore.totalStamps >= 3 && stampStore.totalStamps <= 15;
        return requiredFields.every((field) => field) && isValidTotalStamps;
      }
    }
  };

  const goTo = (pageName) => {
    const page = onboardingPages.find((p) => p.name === pageName);
    if (page) {
      activePage.value = page;
    }
  };

  const outlets = ref([]);
  const brandEarningSelection = ref("");
  const brandPoints = ref(1);
  const brandSpending = ref(1);
  const rewards = ref([]);
  function makeDeal(noOfVisits, valueOfRedemption) {
    const aovMultiplier = customerSpendMode.value.includes("per person")
      ? brandIndustry.value.aovMultiplier
      : 1;

    const amount =
      Number(customerSpend.value) *
      Number(noOfVisits) *
      Number(valueOfRedemption) *
      Number(aovMultiplier);

    const points =
      loyaltyProgramType.value === "points"
        ? (noOfVisits * customerSpend.value * aovMultiplier) /
          (brandSpending.value / brandPoints.value)
        : stampStore.totalStamps;

    const currSym = currencySymbol(brandCurrency.value);

    return {
      monetaryCost: decimalPointLarge(amount)
        ? Math.ceil(amount)
        : Math.floor(amount),
      currency:
        currSym +
        currencyFormat(
          decimalPointLarge(amount) ? Math.ceil(amount) : Math.floor(amount)
        ),
      point: decimalPointLarge(points) ? Math.ceil(points) : Math.floor(points),
    };
  }

  const firstDeal = computed(() =>
    makeDeal(noOfVisits.value[0], valueOfRedemption.value[0])
  );
  const secondDeal = computed(() =>
    makeDeal(noOfVisits.value[1], valueOfRedemption.value[1])
  );
  const thirdDeal = computed(() =>
    makeDeal(noOfVisits.value[2], valueOfRedemption.value[2])
  );

  const defaultStampReward = computed(() => {
    const rewardTypeKeys = ["free_item", "cash_discount"];
    const deals = [firstDeal];

    return deals.flatMap((deal) =>
      rewardTypeKeys.map((type) => ({
        type,
        title:
          type === "free_item"
            ? `Free Item worth ${deal.value.currency}`
            : `${deal.value.currency} Off`,
        active: type === "free_item" ? true : false,
        points: deal.value.point,
        rewardValue: deal.value.monetaryCost,
      }))
    );
  });

  const defaultReward = computed(() => {
    const rewardTypeKeys = ["free_item", "cash_discount"];

    const deals = [firstDeal, secondDeal, thirdDeal];
    return deals.flatMap((deal) =>
      rewardTypeKeys.map((type) => ({
        type,
        title:
          type === "free_item"
            ? `Free Item worth ${deal.value.currency}`
            : `${deal.value.currency} Off`,
        active: type === "free_item" ? true : false,
        points: deal.value.point,
        rewardValue: deal.value.monetaryCost,
      }))
    );
  });

  const isRewardValid = computed(() => {
    if (!rewards.value || rewards.value.length === 0) return false;

    return rewards.value.every((reward) => {
      return (
        reward.type &&
        reward.title &&
        reward.points > 0 &&
        reward.rewardValue > 0
      );
    });
  });

  function reset() {
    // restore original types (objects where appropriate) to avoid runtime type errors
    brandCountry.value = {};
    brandTimeZone.value = {};
    brandIndustry.value = {};
    brandName.value = "";
    customerSpend.value = 0;
    customerSpendMode.value = "per transaction";
    brandEarningSelection.value = "";
    brandPoints.value = 1;
    brandSpending.value = 1;
    rewards.value = [];
    outlets.value = [];
    loyaltyProgramType.value = "points";
    activePage.value = onboardingPages[0];
  }

  return {
    brandName,
    brandIndustry,
    brandTimeZone,
    brandCountry,
    brandCurrency,
    customerSpend,
    customerSpendMode,
    brandEarningSelection,
    brandPoints,
    brandSpending,
    rewards,
    outlets,
    loyaltyProgramType,
    valueOfRedemption,
    noOfVisits,
    activePage,
    createdBrand,
    isPageValid,
    goTo,
    makeDeal,
    firstDeal,
    secondDeal,
    thirdDeal,
    defaultStampReward,
    defaultReward,
    reset,
    isRewardValid,
  };
});

export const useBrandStoreCredentials = defineStore("brand-store-credentials", {
  state: () => {
    return {
      outletCredentials: [],
    };
  },
});
export const useOnboardingModalsStore = defineStore(
  "onboardingModalsStore",
  () => {
    const onboardingModal = ref(null);
    const rewardsModal = ref(null);
    const activateModal = ref(null);
    const rewardsStampCardModal = ref(null);

    return {
      onboardingModal,
      rewardsModal,
      rewardsStampCardModal,
      activateModal,
    };
  }
);

export const promotionType = {
  percentage_discount: "Percentage Discount",
  free_shipping: "Free Shipping",
  free_item: "Free Item",
  cash_discount: "Cash Discount",
};

export const isObjectEmpty = (obj) => {
  // treat null/undefined as empty
  if (obj === null || obj === undefined) return true;
  if (typeof obj === "string") return obj.trim() === "";
  if (typeof obj !== "object") return false;
  return Object.keys(obj).length === 0 && obj.constructor === Object;
};

export const onboardingPages = [
  {
    name: "BrandSetup",
    pageNumber: 1,
    component: BrandSetup,
    hideBack: true,
  },
  {
    name: "LoyaltySetup",
    pageNumber: 2,
    component: CustomerSpend,
  },
  {
    name: "StampSetup",
    component: StampSetup,
    isStampOnly: true,
  },
  {
    name: "RewardSetup",
    pageNumber: 3,
    component: RewardSetup,
    isLast: true,
  },
  {
    name: "PointSetup",
    component: PointSetup,
    hideBack: true,
    hideContinue: true,
  },
  {
    name: "AccountCreds",
    component: AccountCreds,
    hideBack: true,
    hideContinue: true,
  },
];

function decimalPointLarge(value) {
  let decimalPart = value - Math.floor(value);
  return decimalPart >= 0.01;
}
